import { SVGProps, useEffect, useRef, useState } from 'react';

import useDebounced from 'lib/hooks/useDebounced';

interface AutoResizableTextProps extends SVGProps<SVGTextElement> {
  payload?: {
    value: string;
  };
  width?: number;
  visibleTicksCount?: number;
}

const split = (payload: string, wordBreaks: number[]): string[] => {
  if (wordBreaks.length === 1) {
    return [payload];
  }
  const results = [];
  let lastIndex = 0;
  for (const nextIndex of wordBreaks) {
    results.push(payload.substr(lastIndex, nextIndex - lastIndex));
    lastIndex = nextIndex;
  }
  return results;
};

const BOX_PADDING = 10;

export const AutoResizableText = (props: AutoResizableTextProps) => {
  const { x, y, payload, textAnchor, width, visibleTicksCount } = props;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [wordBreaks, setWordBreaks] = useState([payload.value.length]);
  const inputRef = useRef(Array(wordBreaks.length));
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const tickWidth = width / visibleTicksCount;
  const [debouncedCallback] = useDebounced(
    () => {
      // wait until we have all of our refs.
      for (const ref of inputRef.current) {
        if (ref == null) {
          return;
        }
      }
      for (let i = 0; i < inputRef.current.length; i++) {
        const ref = inputRef.current[i];
        if (ref.getBBox().width + BOX_PADDING > tickWidth) {
          const cpy = [...wordBreaks];
          const lastValue = cpy[i];
          // reducing by 2 reduces the number of renders
          cpy[i] = lastValue - 2;
          if (i === wordBreaks.length - 1) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            cpy.push(payload.value.length);
          }
          setWordBreaks(cpy);
          return;
        }
        // if we are here it means that we have found an acceptable width (in characters) for the first row.
        // we can then use this to estimate the width of the subsequent rows
      }
    },
    1,
    { leading: true, trailing: true },
  );
  // This starts with the whole string, and gradually breaks it down into smaller parts wrapped in tspan until each part
  // fits within the assigned space for the current tick.
  // Thus, at the end, we will have one line per tspan, and each tspan fits within the width of space given.
  useEffect(debouncedCallback, [payload, tickWidth, wordBreaks]);
  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setWordBreaks([payload.value.length]);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  }, [payload.value.length, tickWidth]);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} textAnchor={textAnchor}>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {split(payload.value, wordBreaks).map((segment: string, index: number) => {
          return (
            <tspan
              ref={(ref) => {
                if (!ref) {
                  return;
                }
                inputRef.current[index] = ref;
              }}
              x={0}
              dy='1.2em'
              key={index}
            >
              {segment}
            </tspan>
          );
        })}
      </text>
    </g>
  );
};
